import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, CheckCircle2, XCircle, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export default function AdminModeration() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [reasonById, setReasonById] = useState({});
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const canAccess = useMemo(() => Boolean(token && user?.is_admin), [token, user]);

  const fetchUsage = async () => {
    if (!canAccess) return;
    setUsageLoading(true);
    try {
      const response = await fetch(`${API}/admin/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to load usage dashboard');
        return;
      }
      setUsage(data || null);
    } catch (error) {
      toast.error('Connection error while loading usage dashboard');
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchReports = async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter) params.set('status', statusFilter);
      const response = await fetch(`${API}/admin/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to load moderation queue');
        return;
      }
      setReports(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Connection error while loading admin reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    if (user && !user.is_admin) {
      navigate('/search');
      return;
    }
    fetchUsage();
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, statusFilter, navigate]);

  const updateStatus = async (reportId, nextStatus) => {
    const reason = (reasonById[reportId] || '').trim();
    if (nextStatus === 'rejected' && !reason) {
      toast.error('Please add a rejection reason');
      return;
    }
    setSavingId(reportId);
    try {
      const response = await fetch(`${API}/admin/reports/${reportId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: nextStatus, reason }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Could not update status');
        return;
      }
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? { ...r, status: data.status, moderation_reason: data.moderation_reason || '' }
            : r
        )
      );
      toast.success(`Report marked ${nextStatus}`);
    } catch (error) {
      toast.error('Connection error while updating status');
    } finally {
      setSavingId(null);
    }
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button onClick={() => navigate('/profile')} variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-heading font-bold text-primary flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Admin Moderation
          </h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Card className="p-5 mb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading font-bold text-foreground">Launch monitoring</div>
              <div className="text-sm text-muted-foreground mt-1">
                Quick usage counters (last {usage?.window_days ?? 7} days).
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchUsage} disabled={usageLoading}>
              {usageLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>

          {usageLoading && !usage ? (
            <div className="text-sm text-muted-foreground mt-4">Loading usage…</div>
          ) : usage ? (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="text-xs text-muted-foreground">Users</div>
                <div className="mt-1 text-2xl font-heading font-bold text-foreground tabular-nums">
                  {usage?.users?.total ?? 0}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="text-xs text-muted-foreground">Reports (last 7d)</div>
                <div className="mt-1 text-2xl font-heading font-bold text-foreground tabular-nums">
                  {usage?.reports?.last_7d ?? 0}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Approved: {usage?.reports?.approved ?? 0} • Pending: {usage?.reports?.pending ?? 0} • Rejected:{' '}
                  {usage?.reports?.rejected ?? 0}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4">
                <div className="text-xs text-muted-foreground">Requests (open)</div>
                <div className="mt-1 text-2xl font-heading font-bold text-foreground tabular-nums">
                  {usage?.requests?.open ?? 0}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Fulfilled: {usage?.requests?.fulfilled ?? 0} • Expired: {usage?.requests?.expired ?? 0}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground mt-4">
              No usage data yet. Try refresh.
            </div>
          )}
        </Card>

        <div className="flex flex-wrap gap-2 mb-4">
          {['approved', 'pending', 'rejected'].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={fetchReports}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading reports...</p>
        ) : reports.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">No reports found for this filter.</Card>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <Card key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-foreground">
                      {r.product_name}
                      {r.brand ? ` • ${r.brand}` : ''}
                      {r.size ? ` (${r.size})` : ''}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {r.store_name} • {r.city} • R{Number(r.price || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      by {r.user_name} • {new Date(r.reported_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-xs px-2 py-1 rounded-full border border-border">
                    status: {r.status || 'approved'}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    type="text"
                    value={reasonById[r.id] ?? r.moderation_reason ?? ''}
                    onChange={(e) =>
                      setReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))
                    }
                    placeholder="Moderation reason (required for reject)"
                    className="min-w-[260px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={savingId === r.id}
                    onClick={() => updateStatus(r.id, 'pending')}
                  >
                    <Clock3 className="w-4 h-4 mr-1" />
                    Pending
                  </Button>
                  <Button
                    size="sm"
                    disabled={savingId === r.id}
                    onClick={() => updateStatus(r.id, 'approved')}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={savingId === r.id}
                    onClick={() => updateStatus(r.id, 'rejected')}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
